import { useMemo } from "react";
import {
	MarkDraw,
	Reveal,
	RevealGroup,
	RevealItem,
} from "@/components/motion/motion";
import { Container, SectionTitle } from "@/components/paper/paper";
import { PrismScene } from "@/components/scenes/PrismScene";
import { useSectionViewTracking } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n/use-i18n";
import {
	ContactSection,
	JourneySection,
	PageHero,
	Section,
	SkillsSection,
} from "../sections/Sections";

const STORY = ["laser", "code", "startups", "freelance", "products"] as const;

export function AboutPage() {
	const { dict, locale } = useI18n();
	const a = dict.about;
	const sectionTracking = useMemo(
		() => [
			{ id: "story", label: "story" },
			{ id: "journey", label: "journey" },
			{ id: "ai", label: "ai" },
			{ id: "skills", label: "skills" },
		],
		[],
	);
	useSectionViewTracking(sectionTracking, { pageType: "other", locale });

	return (
		<>
			<PageHero
				kicker={a.hero.kicker}
				title={a.hero.title}
				intro={a.hero.intro}
			/>
			<Container>
				<PrismScene className="mt-6 md:mt-10" />
			</Container>

			<Section anchor="story">
				<SectionTitle n="01" title={a.story.title} />
				<div className="relative mt-10">
					<span
						className="absolute top-2 bottom-2 left-[7.4rem] hidden w-px border-l border-dashed border-rule-2 md:block"
						aria-hidden="true"
					/>
					<RevealGroup as="ol" className="relative space-y-10">
						{STORY.map((key) => {
							const entry = a.story[key];
							return (
								<RevealItem
									key={key}
									as="li"
									className="relative grid gap-2 md:grid-cols-[7.4rem_1fr] md:gap-10"
								>
									<p className="font-mono text-[0.8rem] text-ink-3 md:pt-1">
										{entry.period}
									</p>
									<div className="relative md:pl-2">
										<span
											className="absolute top-2 -left-[2.85rem] hidden h-2.5 w-2.5 rounded-full bg-ink md:block"
											aria-hidden="true"
										/>
										<h3 className="font-mono text-xl font-semibold text-ink">
											{entry.title}
										</h3>
										<p className="mt-2 max-w-2xl leading-relaxed text-ink-2">
											{entry.body}
										</p>
									</div>
								</RevealItem>
							);
						})}
					</RevealGroup>
				</div>
			</Section>

			<JourneySection n="02" />

			<Section anchor="ai">
				<SectionTitle n="03" title={a.ai.title} />
				<Reveal className="mt-8 flex flex-wrap items-baseline gap-x-6 gap-y-3">
					<p className="font-mono text-[clamp(1.3rem,3vw,2rem)] font-semibold text-ink">
						<MarkDraw>{a.ai.tools}</MarkDraw>
					</p>
					<p className="text-ink-2">{a.ai.body}</p>
				</Reveal>
			</Section>

			<SkillsSection n="04" />
			<ContactSection n="05" />
		</>
	);
}
